import { gql } from "@apollo/client";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import apollo, { API_CMS_PROVIDER } from "@/helpers/apollo";
import BlogPost, { type BlogPostProps } from "@/pages/blog/post";

interface BlogPostVariablesType {
  slug: string;
}

const fetchBlogPost = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const response = await apollo().query<BlogPostProps, BlogPostVariablesType>(
      {
        context: { clientName: API_CMS_PROVIDER },
        query: gql`
        query Blogs($slug: String!) {
          blogs(filters: { slug: { eq: $slug } }) {
            data {
              attributes {
                title
                slug
                body
                metaDescription
                author {
                  data {
                    attributes {
                      name
                      bio
                      avatar {
                        data {
                          attributes {
                            formats
                          }
                        }
                      }
                    }
                  }
                }
                publishedAt
                image {
                  data {
                    attributes {
                      formats
                    }
                  }
                }
              }
            }
          }
        }
      `,
        variables: { slug: data.slug },
      }
    );

    if (!response.data.blogs?.data?.length) {
      throw notFound();
    }

    const blog = response.data.blogs.data[0].attributes;
    const formats = blog.image.data.attributes.formats;
    const largestImage = Object.values(formats).reduce(
      (
        accum: { size: number; url: string },
        f: { width: number; height: number; url: string }
      ) => {
        const size = f.width * f.height;
        return size > accum.size ? { size, url: f.url } : accum;
      },
      { size: 0, url: "" }
    );

    return { ...response.data, largestImage };
  });

export const Route = createFileRoute("/blog/$slug")({
  component: BlogPostPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const blog = loaderData.blogs.data[0].attributes;
    const docTitle = `${blog.title} - Morty: Every. Escape room. Ever.`;

    return {
      links: [
        { href: `${process.env.APP_URL}/blog/${blog.slug}`, rel: "canonical" },
      ],
      meta: [
        { title: docTitle },
        { content: blog.metaDescription, name: "description" },
        { content: blog.metaDescription, property: "og:description" },
        { content: loaderData.largestImage.url, property: "og:image" },
        { content: docTitle, property: "og:title" },
        { content: "website", property: "og:type" },
        {
          content: `${process.env.APP_URL}/blog/${blog.slug}`,
          property: "og:url",
        },
        { content: "summary_large_image", name: "twitter:card" },
        { content: "@mortyapp", name: "twitter:site" },
      ],
    };
  },
  headers: () => ({
    "Cache-Control": "s-maxage=3600, stale-while-revalidate=86400",
  }),
  loader: ({ params }) => fetchBlogPost({ data: { slug: params.slug } }),
});

function BlogPostPage() {
  const data = Route.useLoaderData();
  return <BlogPost {...data} />;
}
